import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, Moon, BellOff, AlertTriangle, Download } from "lucide-react";
import segmentsService from "../services/segmentsService";
import { SegmentSubscriber, SegmentStats } from "../types/segment";
import { ReactComponent as InfoIcon } from "../assets/icons/Info.svg";

interface SegmentSubscribersListProps {
  segmentId: string;
  segmentType: "static" | "dynamic";
}

const StatCard: React.FC<{ label: string; value: string; tooltip: string }> = ({ label, value, tooltip }) => (
  <div style={{
    flex: 1,
    padding: "20px",
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "8px" }}>
      <span style={{ fontSize: "14px", color: "#6b7280" }}>{label}</span>
      <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.4, cursor: "help" }} />
        <div className="tooltip">{tooltip}</div>
      </div>
    </div>
    <div style={{ fontSize: "28px", fontWeight: 600 }}>{value}</div>
  </div>
);

const SegmentSubscribersList: React.FC<SegmentSubscribersListProps> = ({ segmentId, segmentType }) => {
  const [subscribers, setSubscribers] = useState<SegmentSubscriber[]>([]);
  const [stats, setStats] = useState<SegmentStats | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccessMessage, setAddSuccessMessage] = useState("");

  useEffect(() => {
    loadSubscribers();
  }, [segmentId, currentPage]);

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const response = await segmentsService.getSegmentSubscribers(segmentId, currentPage);
      setSubscribers(response.subscribers);
      setStats(response.stats);
      setTotalPages(response.total_pages);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error("Failed to load segment subscribers:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "Active": return { backgroundColor: "#d1fae5", color: "#065f46" };
      case "Inactive": return { backgroundColor: "#fef3c7", color: "#92400e" };
      case "Unsubscribed": return { backgroundColor: "#e5e7eb", color: "#374151" };
      case "Bounced": return { backgroundColor: "#fee2e2", color: "#dc2626" };
      default: return { backgroundColor: "#e5e7eb", color: "#374151" };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Active": return <User size={16} />;
      case "Inactive": return <Moon size={16} />;
      case "Unsubscribed": return <BellOff size={16} />;
      case "Bounced": return <AlertTriangle size={16} />;
      default: return <User size={16} />;
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await segmentsService.exportSegmentCsv(segmentId);
    } catch (error) {
      console.error("Failed to export segment:", error);
    } finally {
      setExporting(false);
    }
  };

  const handleAddSubscriber = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAdding(true);
    setAddError("");
    try {
      const result = await segmentsService.addSubscriber(segmentId, { email: addEmail.trim() });
      setAddEmail("");
      setShowAddForm(false);
      loadSubscribers();
      if (result.new_subscriber) {
        setAddError(""); // clear any previous error
        // briefly surface the "new subscriber" note via a success state
        setAddSuccessMessage("New subscriber created and added to segment.");
        setTimeout(() => setAddSuccessMessage(""), 4000);
      }
    } catch (err: any) {
      setAddError(err.response?.data?.error || "Failed to add subscriber");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSubscriber = async (subscriberId: string) => {
    setRemovingId(subscriberId);
    try {
      await segmentsService.removeSubscriber(segmentId, subscriberId);
      loadSubscribers();
    } catch (error) {
      console.error("Failed to remove subscriber:", error);
    } finally {
      setRemovingId(null);
    }
  };

  const formatPercent = (rate: number) => `${(rate * 100).toFixed(1)}%`;

  if (loading) {
    return <div style={{ padding: "20px", color: "#666" }}>Loading subscribers...</div>;
  }

  return (
    <div>
      {/* Stat cards */}
      {stats && totalCount > 0 && (
        <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
          <StatCard
            label="Unique open rate"
            value={formatPercent(stats.unique_open_rate)}
            tooltip="Distinct editions opened / total deliveries. Each edition counts at most once per subscriber."
          />
          <StatCard
            label="Total open rate"
            value={formatPercent(stats.open_rate)}
            tooltip="Total open events / total deliveries. Includes repeat opens of the same edition."
          />
          <StatCard
            label="Unique click rate"
            value={formatPercent(stats.unique_click_rate)}
            tooltip="Distinct editions clicked / total deliveries. Each edition counts at most once per subscriber."
          />
          <StatCard
            label="Total click rate"
            value={formatPercent(stats.click_rate)}
            tooltip="Total click events / total deliveries. Includes repeat clicks on the same edition."
          />
          <StatCard
            label="Verified click rate"
            value={formatPercent(stats.verified_click_rate)}
            tooltip="Verified clicks use a proprietary formula to confirm a subscriber actually clicked. This is a lower bound on real clicks."
          />
          <StatCard
            label="Unsubscribes"
            value={stats.unsubscribes.toLocaleString()}
            tooltip="Number of subscribers in this segment with Unsubscribed status"
          />
          <StatCard
            label="Unsubscribe rate"
            value={formatPercent(stats.unsubscribe_rate)}
            tooltip="Percentage of subscribers in this segment who have unsubscribed"
          />
        </div>
      )}
      {segmentType === "static" && totalCount > 0 && (
        <p style={{ marginTop: "-16px", marginBottom: "20px", fontSize: "12px", color: "#9ca3af" }}>
          Stats are based on this segment's rules and do not update when subscribers are manually added or removed.
        </p>
      )}

      {/* Subscriber table */}
      <div style={{
        padding: "16px",
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <h3 style={{ margin: 0 }}>Subscribers</h3>
            <span style={{
              fontSize: "12px",
              color: "#6b7280",
              backgroundColor: "#f3f4f6",
              padding: "2px 8px",
              borderRadius: "10px",
            }}>
              Showing {subscribers.length} of {totalCount.toLocaleString()} results
            </span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => { setShowAddForm((v) => !v); setAddError(""); setAddEmail(""); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                backgroundColor: "#000",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Add Subscriber
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || totalCount === 0}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                fontSize: "13px",
                fontWeight: 500,
                color: exporting ? "#9ca3af" : "#374151",
                backgroundColor: "#fff",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                cursor: exporting || totalCount === 0 ? "not-allowed" : "pointer",
              }}
            >
              <Download size={16} />
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
          </div>
        </div>

        {showAddForm && (
          <form onSubmit={handleAddSubscriber} style={{ margin: "12px 0", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <input
              type="email"
              placeholder="subscriber@example.com"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              style={{
                padding: "6px 10px",
                fontSize: "13px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                width: "260px",
              }}
              autoFocus
            />
            <button
              type="submit"
              disabled={adding || !addEmail.trim()}
              style={{
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                backgroundColor: adding ? "#6b7280" : "#000",
                border: "none",
                borderRadius: "6px",
                cursor: adding || !addEmail.trim() ? "not-allowed" : "pointer",
              }}
            >
              {adding ? "Adding..." : "Add"}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setAddEmail(""); setAddError(""); }}
              style={{ padding: "6px 10px", fontSize: "13px", background: "none", border: "none", color: "#6b7280", cursor: "pointer" }}
            >
              Cancel
            </button>
            {addError && <span style={{ fontSize: "13px", color: "#dc2626" }}>{addError}</span>}
            {segmentType === "dynamic" && !addError && (
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Note: Manually added subscribers won't appear in this list until the next nightly refresh.
              </span>
            )}
          </form>
        )}

        {addSuccessMessage && (
          <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#059669" }}>{addSuccessMessage}</p>
        )}
        <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#9ca3af" }}>
          Subscribers that make up this segment
        </p>

        {subscribers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#666", backgroundColor: "#f9fafb", borderRadius: "8px" }}>
            <p>{segmentType === "static" ? "No subscribers yet. Use the Add Subscriber button above." : "No subscribers match this segment's rules."}</p>
          </div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
                  <th style={{ padding: "8px 12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", fontSize: "12px" }}>Email</th>
                  <th style={{ padding: "8px 12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", fontSize: "12px" }}>Status</th>
                  <th style={{ padding: "8px 12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", fontSize: "12px" }}>Unique Open Rate</th>
                  <th style={{ padding: "8px 12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", fontSize: "12px" }}>Unique Click Rate</th>
                  <th style={{ padding: "8px 12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", fontSize: "12px", width: "80px" }}></th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub) => (
                  <tr key={sub.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "8px 12px" }}>
                      <Link to={`/subscribers/${sub.id}`} style={{ color: "#5d5ded", textDecoration: "none" }}>
                        {sub.email}
                      </Link>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <span
                        className="newsletter-status"
                        style={{ ...getStatusStyle(sub.status), display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", borderRadius: "12px", fontSize: "12px" }}
                      >
                        {getStatusIcon(sub.status)}
                        {sub.status}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px" }}>{formatPercent(sub.unique_open_rate)}</td>
                    <td style={{ padding: "8px 12px" }}>{formatPercent(sub.unique_click_rate)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      <button
                        onClick={() => handleRemoveSubscriber(sub.id)}
                        disabled={removingId === sub.id}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          color: "#dc2626",
                          backgroundColor: "transparent",
                          border: "1px solid #fca5a5",
                          borderRadius: "4px",
                          cursor: removingId === sub.id ? "not-allowed" : "pointer",
                          opacity: removingId === sub.id ? 0.5 : 1,
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ textAlign: "center", margin: "16px 0", display: "flex", justifyContent: "center", gap: "4px" }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                >
                  &lt;&lt;
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                >
                  &lt;
                </button>
                <span style={{ padding: "6px 12px", fontSize: "14px" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                >
                  &gt;
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="subscriber-pagination-end-button"
                  style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                >
                  &gt;&gt;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SegmentSubscribersList;
